import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastBarService } from './toast-bar.service';

@Injectable({ providedIn: 'root' })
export class ValidateInputService {

  toastBarService = inject(ToastBarService);

  private readonly states = new Map<string, boolean>();

  private readonly enableSubject = new BehaviorSubject<boolean>(true);
  readonly inputEnabled$ = this.enableSubject.asObservable();

  // called by every BlockTextComponent whenever its validity changes
  reportValidity(id: string, isValid: boolean): void {
    this.states.set(id, isValid);

    this.checkValidity();
  }

  removeBlockFromStates(id: string): void {
    this.states.delete(id);

    this.checkValidity();
  }

  /**
   * Checks the validity of all input states and emits the result.
   *
   * This method evaluates whether all values in the `states` map are `true`.
   * If every state is valid, it emits `true` to the `enableSubject`; otherwise, it emits `false`.
   *
   * @private
   */
  private checkValidity() {
    this.enableSubject.next(
      [...this.states.values()].every((value) => value === true)
    );
  }

  // remove states when a component is destroyed
  unregister(): void {
    this.states.clear();
    // now that no fields remain, inputs are (by default) allowed again:
    this.enableSubject.next(true);
  }

   highlightInvalidEditorBlocks() {
    let noEmptyFields = true;
    let allElements = document.querySelectorAll('.ql-editor');
    allElements.forEach((el) => {
      let parentElement = el.parentElement?.parentElement as HTMLElement;
      if (!el.textContent && !parentElement.classList.contains('can-be-empty')) {
        noEmptyFields = false;
        parentElement.style.outline = '2px solid red';
        // reset border styling
      } else {
        parentElement.style.outline = '0';
      }
    });

    // highlight all blocks that have too many characters
    allElements = document.querySelectorAll<HTMLElement>('.too-many-chars');

    allElements.forEach((el) => {
      // This will skip text nodes, comments, etc.
      const editor = el.previousElementSibling;

      // Make sure it’s actually an HTMLElement before touching .style
      if (editor instanceof HTMLElement) {
        editor.style.outline = '2px solid red';
      }
    });
    return noEmptyFields;
  }

  /**
   * Outlines the editors that hold duplicate text, keyed by the stable
   * `${blockId}::${fieldName}` stamped on each `<quill-editor>` (see
   * block-text.component.html). Matching by key — not text content — keeps the
   * highlight scoped to exactly the offending fields. Must run AFTER
   * `highlightInvalidEditorBlocks()`, whose reset pass would otherwise clear it.
   */
  highlightDuplicateEditorBlocks(duplicateFieldKeys: Set<string>): void {
    if (!duplicateFieldKeys.size) return;

    document.querySelectorAll<HTMLElement>('quill-editor[data-field-key]').forEach((el) => {
      const key = el.getAttribute('data-field-key');
      if (key && duplicateFieldKeys.has(key)) {
        el.style.outline = '2px solid red';
      }
    });
  }
}
